<template>
	<li class="vcx-podcast-article-card">
		<article>
			<div class="vcx-podcast-article-card__image-wrapper">
				<img v-if="imageUrl" :src="imageUrl" :alt="imageAlt" class="vcx-podcast-article-card__image" />
			</div>
			<div class="vcx-podcast-article-card__content">
				<div>
					<p class="vcx-subheading-bold">{{ podcast.title }}</p>
					<p v-if="podcast.description" class="vcx-body-md vcx-text-gray-30">{{ podcast.description }}</p>
				</div>
				<div class="vcx-podcast-article-card__listen-links">
					<a
						v-if="podcast.applePodcastLink"
						:href="podcast.applePodcastLink"
						target="_blank"
						rel="noopener noreferrer"
						class="vcx-podcast-article-card__listen-link vcx-body-md vcx-text-gray-30"
						data-test="apple-podcast-link"
						@click="trackPodcastClick(podcast.title, podcast.applePodcastLink)"
					>
						<BaseSvgIcon
							name="video-play"
							width="16"
							height="16"
							aria-hidden="true"
							class="vcx-podcast-article-card__listen-icon"
						/>
						Listen on Apple
						<span class="show-for-sr">for {{ podcast.title }}</span>
					</a>
					<a
						v-if="podcast.spotifyPodcastLink"
						:href="podcast.spotifyPodcastLink"
						target="_blank"
						rel="noopener noreferrer"
						class="vcx-podcast-article-card__listen-link vcx-body-md vcx-text-gray-30"
						data-test="spotify-podcast-link"
						@click="trackPodcastClick(podcast.title, podcast.spotifyPodcastLink)"
					>
						<BaseSvgIcon
							name="video-play"
							width="16"
							height="16"
							aria-hidden="true"
							class="vcx-podcast-article-card__listen-icon"
						/>
						Listen on Spotify
						<span class="show-for-sr">for {{ podcast.title }}</span>
					</a>
				</div>
			</div>
		</article>
	</li>
</template>

<script setup lang="ts">
import { MXP_SIV_NEWSROOM } from '@constants/vcx';
import { useMixpanel } from '@utils/composables/use-mixpanel';
import { useSanityImage } from '@utils/composables/use-sanity-image';
import { SanityFeaturedPodcast } from 'types/sanity/vcx-landing-page';

interface Props {
	podcast: SanityFeaturedPodcast;
}

const props = defineProps<Props>();
const { trackMixpanelClick } = useMixpanel();
const imageUrl = useSanityImage(props.podcast.podcastImage?.asset?.url, 'thumbnail');
const imageAlt = props.podcast.podcastImage?.asset?.altText ?? props.podcast.title;

function trackPodcastClick(podcastTitle: string, podcastUrl: string) {
	trackMixpanelClick(
		{
			'Action Element': 'Podcast Link',
			'Action Target': podcastTitle,
			'Action Context': MXP_SIV_NEWSROOM['View ID'],
			Link: podcastUrl
		},
		true
	);
}
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive' as *;

.vcx-podcast-article-card {
	position: relative;
	width: 400px;
	flex-shrink: 0;
	white-space: normal;
	border-radius: var(--vcx-radius-50);
	overflow: hidden;

	@include vcx-media-smo {
		width: 320px;
	}

	> article {
		display: flex;
		flex-direction: column;
		height: 100%;
		border: 1px solid var(--vcx-border-dark);
		border-radius: var(--vcx-radius-50);
	}

	&__image-wrapper {
		aspect-ratio: 16 / 9;
		background-color: var(--vcx-gray-95);
		border-radius: var(--vcx-radius-50) var(--vcx-radius-50) 0 0;
		overflow: hidden;
	}

	&__image {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__content {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding: 32px;
		gap: 24px;
		flex: 1;
		min-width: 0;
	}

	&__listen-links {
		display: flex;
		flex-wrap: wrap;
		gap: 16px 48px;
	}

	&__listen-icon {
		color: var(--vcx-brand-primary);
	}

	&__listen-link {
		display: flex;
		align-items: center;
		gap: 4px;
		text-decoration: none;
		white-space: nowrap;

		&:hover {
			text-decoration: underline;
		}
	}
}
</style>
